import he from 'he';
import fp from 'lodash/fp';
import React from 'react';
import { Badge, Breadcrumb, BreadcrumbItem, Button, Col, Grid, Row } from 'react-bootstrap';
import { FiArrowDown } from 'react-icons/fi';
import { connect } from 'react-redux';
import { LinkContainer } from 'react-router-bootstrap';
import Spinner from 'react-spinkit';
import { branch, compose, lifecycle, renderComponent, withHandlers, withProps, withStateHandlers } from 'recompose';
import { bindActionCreators } from 'redux';
import { routes } from '../../../../constants';
import { fullName, isBusinessAdmin, isTeamManager } from '../../../../services';
import { businessActions, businessCurrentTeamMembersSelector, businessCurrentTeamStatsSelector, businessSelector, businessTeamsSelector, businessCurrentTeamNonMemberManagersSelector } from '../../../../state/modules/business';
import { currentUserSelector } from '../../../../state/modules/user';
import { pluralise, unfixedFloor } from '../../../../utils';
import { Icon, SortableTable, UserAvatar } from '../../../components';
import SearchForm from '../../../components/search-form';
import { ProgressChart } from '../../../partials';
import Loading from '../../loading';
import NumberBadge from '../partials/number-badge';

const TeamMembersHeader = ({
  currentTeamStats: { businessTeam },
  user,
}) => (
    <section id='header--teams' key='team-header' className='layout--header'>
      <Grid>
        <Row className='button-gutter'>
          <Col xs={12}>
            <Breadcrumb>
              <LinkContainer to={routes.app.teams.root}>
                <BreadcrumbItem>
                  Teams
                </BreadcrumbItem>
              </LinkContainer>
              <BreadcrumbItem active>
                {he.decode(businessTeam.name)}
              </BreadcrumbItem>
            </Breadcrumb> 
            <div className='flexbox justify-between align-baseline'>
              <div>
                <h1>
                  <i className='icon-team-large' /> {he.decode(businessTeam.name)}
                </h1>
              </div>
              <div>
                {isBusinessAdmin(user) ?
                  <LinkContainer className='edit-team-link' to={routes.app.teams.teamEdit(businessTeam.id)}>
                    <div>
                      <Icon icon='edit-small' className='edit-team-icon' />
                      <span className='edit-team'>Edit team</span>
                    </div>
                  </LinkContainer> : null
                }
              </div>
            </div>
          </Col>
        </Row>
      </Grid>
    </section>
  );

export const teamMemberColumns = [
  {
    name: 'memberName',
    display: () => 'Name',
    className: () => 'member-name',
    sortable: true
  },
  {
    name: 'progress',
    display: () => 'CPD completion',
    className: () => '',
    sortable: true
  },
  {
    name: 'cpdReview',
    display: () => 'CPD review',
    className: () => 'cpdReview'
  },
  {
    name: 'seeUser',
    display: () => '',
    className: () => 'operations'
  }
]

const seeUser = (route) =>
  <LinkContainer
    className='pull-right'
    to={route}>
    <Button bsStyle='plain'><Icon icon='chevron-right-large' /></Button>
  </LinkContainer>

const TeamMembersDetails = branch(
  ({ currentTeamMembers }) => !currentTeamMembers,
  renderComponent(Loading)
)(({
  businessTeamId,
  loadMoreButton,
  columns,
  rows,
  searchQuery,
  updateSearchQuery,
  sortBy,
  isUpdatingSearchQuery,
  teamProgress,
  teamSize,
  pendingExternalCpdCount,
  teamManagers
}) => {
  const bannerItemClass = 'banner-item col-md-3';
  return (
    <section id='section--teams' className='layout--section'>
      <div className='container'>
        <div className='progress--banner'>
          <Row>
            <Col xs={12} className={bannerItemClass}>
              <div className='chart'>
                <ProgressChart progressPercentage={teamProgress} width={120} height={120} />
              </div>
              <div className='info'>
                <p>Your team's overall CPD progress</p>
              </div>
            </Col>
            <Col xs={12} className={bannerItemClass}>
              <div className='chart'>
                <NumberBadge value={teamSize} width={120} height={120} />
              </div>
              <div className='info'>
                <p>{pluralise('CPD Member', teamSize)}</p>
              </div>
            </Col>
            <Col xs={12} className={bannerItemClass}>
              <LinkContainer className='chart clickable' to={`${routes.app.externalCpd.reviewTeam(businessTeamId)}`}>
                <div>
                  <Icon className='teams-icon' icon='cpd-activity-large' />
                  <Badge bsClass={`teams-notification ${pendingExternalCpdCount > 0 ? 'teams-badge-red' : 'teams-badge-green'}`}>
                    {
                      pendingExternalCpdCount > 0 ?
                        pendingExternalCpdCount < 100 ? pendingExternalCpdCount : '99+'
                        : <Icon icon='accept-thick' />
                    }
                  </Badge>
                </div>
              </LinkContainer>
              <LinkContainer className='info clickable' to={`${routes.app.externalCpd.reviewTeam(businessTeamId)}`}>
                <div>
                  <p>
                    {
                      pendingExternalCpdCount > 0 ?
                        `Review ${pendingExternalCpdCount} CPD ${pluralise('item', pendingExternalCpdCount)}` : 'No CPD for review'
                    }
                  </p>
                </div>
              </LinkContainer>
            </Col>
            <Col xs={12} className='banner-item col-md-3'>
              <LinkContainer className='chart clickable' to={routes.app.teams.singleTeamProgressReport(businessTeamId)}>
                <div>
                  <Icon className='teams-icon' icon='report-download' />
                  <Badge bsClass='teams-notification teams-badge-green'><FiArrowDown /></Badge>
                </div>
              </LinkContainer>
              <LinkContainer className='info clickable' to={routes.app.teams.singleTeamProgressReport(businessTeamId)}>
                <div>
                  <p>
                    Download progress report
                      </p>
                </div>
              </LinkContainer>
            </Col>
          </Row>
        </div>
        <Row>
          <Col xs={12}>
            <h2>Team managers</h2>
          </Col>
        </Row>
        <Row>
          <Col xs={12}>
            {
              teamManagers.map((manager, index) => (
                <Badge key={index} bsClass='team-manager-badge'>{manager.firstName} {manager.lastName}</Badge>
              ))
            }
          </Col>
        </Row>
        <Row>
          <Col xs={12} md={6}>
            <h2>Team members</h2>
          </Col>
          <Col xs={12} md={6}>
            <SearchForm id='search-desktop' label='Search Members' className='searchDesktop' searchQuery={searchQuery} onSearchChange={updateSearchQuery} />
          </Col>
        </Row>
        <SortableTable
          className='table-team-members'
          rows={rows}
          columns={pendingExternalCpdCount > 0 ? columns : fp.remove(({ name }) => name === 'cpdReview')(columns)}
          sortBy={sortBy}
          isFetchingAll={isUpdatingSearchQuery}
          emptyData={searchQuery && searchQuery !== '' ? `No team members match "${searchQuery}"` : 'This team currently has no members'} />
        {loadMoreButton}
      </div>
    </section>
  )
})

const TeamMembersPage = (props) => (
  <div className='base-layout'>
    <TeamMembersHeader key='team-header' {...props} />
    <TeamMembersDetails key='team-details' {...props} />
  </div>
);

const defaultTake = 20;

const mapStateToProps = (state) => ({
  user: currentUserSelector(state),
  business: businessSelector(state),
  businessTeamStats: businessTeamsSelector(state),
  currentTeamStats: businessCurrentTeamStatsSelector(state),
  currentTeamMembers: businessCurrentTeamMembersSelector(state),
  currentTeamNonMemberManagers: businessCurrentTeamNonMemberManagersSelector(state),
  isFetchingMembers: state.business.isFetchingMembers
});

const mapDispatchToProps = (dispatch) => ({
  getTeamStats: bindActionCreators(businessActions.business.team.getTeamStats, dispatch),
  getTeamMembers: bindActionCreators(businessActions.business.team.getTeamMembers, dispatch),
  getNonMemberManagers: bindActionCreators(businessActions.business.team.getNonMemberManagers, dispatch),
  loadMore: bindActionCreators(businessActions.business.team.loadMoreMembers, dispatch)
});

export default compose(
  withProps(({
    match: {
      params: {
        businessTeamId
      }
    }
  }) => ({
    businessTeamId: parseInt(businessTeamId, 10)
  })),
  connect(mapStateToProps, mapDispatchToProps),
  lifecycle({
    componentDidMount() {
      const {
        business,
        getTeamStats,
        getTeamMembers,
        getNonMemberManagers,
        businessTeamId
      } = this.props

      getTeamStats(business.id, businessTeamId);

      getTeamMembers(business.id, businessTeamId, { teamId: businessTeamId, skip: 0, take: defaultTake });

      getNonMemberManagers(business.id, businessTeamId);
    }
  }),
  branch(
    ({ business }) => !business,
    renderComponent(Loading)
  ),
  branch(
    ({ currentTeamStats, currentTeamMembers }) => !currentTeamStats || !currentTeamMembers,
    renderComponent(Loading)
  ),
  withProps(({ business, currentTeamMembers, currentTeamStats, user }) => ({
    teamProgress: fp.get('stats.stats.totalProgressPercentage')(currentTeamStats),
    teamMembers: currentTeamMembers ? currentTeamMembers.teamMembers : [],
    pendingExternalCpd: currentTeamMembers ? currentTeamMembers.pendingExternalCpd : [],
    isTeamManager: !isBusinessAdmin(user) && isTeamManager(user, business),
  })),
  withProps(({ user, isTeamManager, currentTeamStats, pendingExternalCpd }) => ({
    pendingExternalCpdCount: isTeamManager ? // Minus team manager self records count
      fp.get('stats.pendingExternalCpdCount')(currentTeamStats) -  fp.filter(({ userId }) => userId === user.id)(pendingExternalCpd).length :
      fp.get('stats.pendingExternalCpdCount')(currentTeamStats)
  })),
  withProps(({ teamMembers }) => ({
    teamSize: teamMembers.length
  })),
  withProps(({ teamMembers, currentTeamNonMemberManagers }) => ({
    teamManagers: currentTeamNonMemberManagers.concat(fp.flow(
      fp.filter((member) => member.isTeamManager),
      fp.map(fp.get('user'))
    )(teamMembers))
  })),
  withStateHandlers(() => (
    {
      searchQuery: '',
      searchDebounce: undefined,
      isUpdatingSearchQuery: false,
      sort: undefined
    }), {
      updateSearchDebounce: () => (searchDebounce) => ({ searchDebounce }),
      updateSearchQuery: () => (e) => ({ searchQuery: e.target.value, isUpdatingSearchQuery: true }),
      finishSearchQueryUpdate: () => () => ({ isUpdatingSearchQuery: false }),
      updateSort: () => (sort) => ({ sort })
    }
  ),
  withProps(
    ({ businessTeamId, currentTeamMembers, searchQuery, sort }) => ({
      hasMoreToLoad: fp.get('teamMembers.length')(currentTeamMembers) < fp.get('hits')(currentTeamMembers),
      teamMemberFilter: {
        teamId: businessTeamId,
        skip: 0,
        take: defaultTake,
        tags: searchQuery,
        sort
      },
      totalTeamMemberCount: fp.get('total')(currentTeamMembers)
    })
  ),
  withHandlers({
    loadMore: ({ business, businessTeamId, loadMore, teamMembers, teamMemberFilter }) => () => {
      const query = {
        ...teamMemberFilter,
        skip: teamMembers.length
      }
      loadMore(business.id, businessTeamId, query)
    },
    refetchTeamMembers: ({
      business,
      businessTeamId,
      getTeamMembers,
      teamMemberFilter
    }) => () => getTeamMembers(business.id, businessTeamId, teamMemberFilter),
    sortBy: ({ updateSort }) => (field, sortOrder) => {
      const sortParam = sortOrder ? `${field},${sortOrder}` : undefined;
      updateSort(sortParam)
    }
  }),
  lifecycle({
    componentWillReceiveProps({
      searchDebounce,
      updateSearchDebounce,
      refetchTeamMembers,
      teamMemberFilter,
      isFetchingMembers,
      finishSearchQueryUpdate
    }) {
      if (!fp.isEqual(this.props.teamMemberFilter, teamMemberFilter)) {
        const debounced = fp.debounce(500)(refetchTeamMembers);
        updateSearchDebounce(debounced);
        searchDebounce && searchDebounce.cancel();
        debounced();
      }
      if (this.props.isFetchingMembers && !isFetchingMembers) {
        finishSearchQueryUpdate();
      }
    }
  }),
  withProps(
    ({
      teamMembers,
      pendingExternalCpd,
      businessTeamId,
      isTeamManager,
      user
    }) => ({
      columns: teamMemberColumns,
      rows: fp.map((member) => ({
        memberName: <LinkContainer to={routes.app.teams.userProfiles(member.user.id)}>
          <div>
            <UserAvatar userName={fullName(member.user)} avatar={member.user.avatar} />
            <span>
              {fullName(member.user)}
              {member.isTeamManager ? <Badge bsClass='team-manager-badge'>Team manager</Badge> : null}
            </span>
          </div>
        </LinkContainer>,
        progress: `${unfixedFloor(member.progress, 0)}%`,
        cpdReview: fp.flow(
          fp.filter(({ userId }) => userId === member.user.id),
          (cpdReviews) => cpdReviews.length > 0 ?
            isTeamManager && user.id === member.user.id ? // Team manager cannot approve self records
              <Badge bsClass='team-notification unclickable'>{cpdReviews.length > 99 ? '99+' : cpdReviews.length}</Badge> :
              <LinkContainer to={routes.app.externalCpd.reviewTeamMember(businessTeamId, member.user.id)}>
                <Badge bsClass='team-notification'>{cpdReviews.length > 99 ? '99+' : cpdReviews.length}</Badge>
              </LinkContainer>
            : null
        )(pendingExternalCpd),
        seeUser: seeUser(routes.app.teams.userProfiles(member.user.id))
      })
      )(teamMembers)
    })
  ),
  withProps(
    ({
      hasMoreToLoad,
      isFetchingMembers,
      loadMore
    }) => ({
      loadMoreButton: hasMoreToLoad ?
        <Row>
          <Col xs={4} xsOffset={4}>
            {
              isFetchingMembers ?
                <Button><Spinner name='circle' className='spinner' fadeIn='none' /></Button> :
                <Button className='full' onClick={loadMore}>Load More</Button>
            }
          </Col>
        </Row> : null,
    })
  )
)(TeamMembersPage)



// WEBPACK FOOTER //
// ./src/containers/pages/app/teams/team-members.js